import type { MouseEvent as ReactMouseEvent, RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, Decoration, type DecorationSet, type MouseSelectionStyle, WidgetType } from "@codemirror/view";
import { EditorState, EditorSelection, type SelectionRange, StateField, StateEffect, Compartment } from "@codemirror/state";
import { markdown } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching } from "@codemirror/language";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { autocompletion, completionKeymap } from "@codemirror/autocomplete";
import {
  useOpenFile,
  useStoreDispatch,
  useLoadFileContent,
  useHighlight,
  useTtsPlayback,
} from "../store/StoreContext.js";
import { useAuth } from "../context/AuthContext.js";
import { Play, ChatCircle, MagnifyingGlass, PencilLine } from "@phosphor-icons/react";

const LIVE_BROADCAST_DELAY = 30;  // ms — broadcast to other clients
const DISK_WRITE_DELAY = 500;     // ms — persist to disk
const VIEWPORT_INTERACTION_COOLDOWN_MS = 1500;
const AUTO_SCROLL_GRACE_MS = 200;
const VIEWPORT_VISIBILITY_MARGIN_PX = 80;

function isWordChar(ch: string): boolean {
  return /[A-Za-z0-9'_’-]/.test(ch);
}

function getWordStartOffset(lineText: string, relativePos: number): number {
  if (!lineText) return relativePos;
  let i = Math.max(0, Math.min(relativePos, lineText.length - 1));
  if (!isWordChar(lineText[i]) && i > 0 && isWordChar(lineText[i - 1])) {
    i -= 1;
  }
  if (!isWordChar(lineText[i])) return relativePos;
  while (i > 0 && isWordChar(lineText[i - 1])) i -= 1;
  return i;
}

function posFromSelection(view: EditorView): number | null {
  const selection = view.dom.ownerDocument.getSelection();
  if (!selection) return null;

  const candidates = [
    { node: selection.focusNode, offset: selection.focusOffset },
    { node: selection.anchorNode, offset: selection.anchorOffset },
  ];

  for (const candidate of candidates) {
    if (!candidate.node || !view.dom.contains(candidate.node)) continue;
    try {
      return view.posAtDOM(candidate.node, candidate.offset);
    } catch {
      // Ignore DOM positions CodeMirror can't map right now.
    }
  }

  return null;
}

function posFromCaretPoint(view: EditorView, event: MouseEvent): number | null {
  const doc = view.dom.ownerDocument as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (
      x: number,
      y: number,
      options?: unknown
    ) => { offsetNode: Node; offset: number } | null;
  };

  if (typeof doc.caretRangeFromPoint === "function") {
    try {
      const range = doc.caretRangeFromPoint(event.clientX, event.clientY);
      if (range && view.dom.contains(range.startContainer)) {
        return view.posAtDOM(range.startContainer, range.startOffset);
      }
    } catch {
      // Safari can transiently hand back DOM state CodeMirror can't resolve.
    }
  }

  if (typeof doc.caretPositionFromPoint === "function") {
    try {
      const caret = doc.caretPositionFromPoint(event.clientX, event.clientY);
      if (caret && view.dom.contains(caret.offsetNode)) {
        return view.posAtDOM(caret.offsetNode, caret.offset);
      }
    } catch {
      // Ignore and fall through to safer fallbacks.
    }
  }

  return null;
}

function safePosAtCoords(view: EditorView, event: MouseEvent): number | null {
  try {
    return view.posAtCoords({ x: event.clientX, y: event.clientY });
  } catch {
    return null;
  }
}

function resolveContextMenuPos(view: EditorView, event: MouseEvent): number | null {
  const fromCaretPoint = posFromCaretPoint(view, event);
  if (fromCaretPoint !== null) return fromCaretPoint;

  const fromSelection = posFromSelection(view);
  if (fromSelection !== null) return fromSelection;

  const target = event.target;
  if (target instanceof Node && view.dom.contains(target)) {
    try {
      return view.posAtDOM(target, 0);
    } catch {
      // Fall through to selection fallback.
    }
  }

  return view.state.selection.main.head;
}

function getWordEndOffset(lineText: string, relativePos: number): number {
  if (!lineText) return relativePos;
  let i = Math.max(0, Math.min(relativePos, lineText.length));
  if (i > 0 && !isWordChar(lineText[Math.min(i, lineText.length - 1)]) && isWordChar(lineText[i - 1])) {
    i -= 1;
  }
  if (!lineText[i] || !isWordChar(lineText[i])) {
    if (i > 0 && isWordChar(lineText[i - 1])) {
      while (i < lineText.length && isWordChar(lineText[i])) i += 1;
      return i;
    }
    return relativePos;
  }
  while (i < lineText.length && isWordChar(lineText[i])) i += 1;
  return i;
}

function resolveMouseSelectionPos(view: EditorView, event: MouseEvent): number | null {
  const fromCaretPoint = posFromCaretPoint(view, event);
  if (fromCaretPoint !== null) return fromCaretPoint;

  const fromSelection = posFromSelection(view);
  if (fromSelection !== null) return fromSelection;

  const target = event.target;
  if (target instanceof Node && view.dom.contains(target)) {
    try {
      return view.posAtDOM(target, 0);
    } catch {
      // Ignore and fall through.
    }
  }

  return safePosAtCoords(view, event) ?? view.state.selection.main.head;
}

function rangeForResolvedClick(
  view: EditorView,
  pos: number,
  clickType: number
): SelectionRange {
  if (clickType <= 1) {
    return EditorSelection.cursor(pos);
  }

  const line = view.state.doc.lineAt(pos);
  if (clickType === 2) {
    const relativePos = Math.max(0, Math.min(line.length, pos - line.from));
    const from = line.from + getWordStartOffset(line.text, relativePos);
    const to = line.from + getWordEndOffset(line.text, relativePos);
    if (from !== to) {
      return EditorSelection.range(from, to);
    }
    return EditorSelection.cursor(pos);
  }

  const to = line.to < view.state.doc.length ? line.to + 1 : line.to;
  return EditorSelection.range(line.from, to);
}

function safeMouseSelectionStyle(
  view: EditorView,
  event: MouseEvent
): MouseSelectionStyle | null {
  if (event.button !== 0) return null;

  const startPos = resolveMouseSelectionPos(view, event);
  if (startPos === null) return null;

  const clickType = Math.min(3, Math.max(1, event.detail || 1));
  let mappedStartPos = startPos;
  let startSelection = view.state.selection;

  return {
    update(update) {
      if (update.docChanged) {
        mappedStartPos = update.changes.mapPos(mappedStartPos);
        startSelection = startSelection.map(update.changes);
      }
    },
    get(curEvent: MouseEvent, extend: boolean, multiple: boolean) {
      const currentPos = resolveMouseSelectionPos(view, curEvent) ?? mappedStartPos;
      let range = rangeForResolvedClick(view, currentPos, clickType);

      if (clickType === 1 && currentPos !== mappedStartPos && !extend) {
        range = EditorSelection.range(mappedStartPos, currentPos);
      } else if (extend) {
        range = EditorSelection.range(startSelection.main.anchor, range.head);
      }

      if (multiple) {
        return EditorSelection.create([...startSelection.ranges, range]);
      }
      return EditorSelection.create([range]);
    },
  };
}

function isPosComfortablyVisible(
  view: EditorView,
  pos: number,
  wrapper: HTMLElement | null,
  marginPx = VIEWPORT_VISIBILITY_MARGIN_PX
): boolean {
  if (!wrapper || pos < view.viewport.from || pos > view.viewport.to) return false;
  const coords = view.coordsAtPos(pos);
  if (!coords) return false;

  const rect = wrapper.getBoundingClientRect();
  const margin = Math.max(0, Math.min(marginPx, rect.height / 4));
  return coords.bottom >= rect.top + margin && coords.top <= rect.bottom - margin;
}

// CodeMirror effect + field for AI/TTS highlights (line decoration for full-line highlight)
const setHighlightEffect = StateEffect.define<{ from: number; to: number } | null>();

const highlightLineDeco = Decoration.line({ class: "cm-ai-highlight" });

const highlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setHighlightEffect)) {
        if (effect.value === null) {
          return Decoration.none;
        }
        const doc = tr.state.doc;
        const decos: any[] = [];
        const fromLine = doc.lineAt(effect.value.from).number;
        const toLine = doc.lineAt(effect.value.to).number;
        for (let i = fromLine; i <= toLine; i++) {
          decos.push(highlightLineDeco.range(doc.line(i).from));
        }
        return Decoration.set(decos);
      }
    }
    return decorations.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

// Dictation ghost text — shows partial transcript inline at cursor
export const setDictationGhostEffect = StateEffect.define<{ pos: number; text: string } | null>();

class DictationGhostWidget extends WidgetType {
  constructor(readonly text: string) { super(); }
  toDOM() {
    const span = document.createElement("span");
    span.className = "dictation-ghost";
    span.textContent = this.text;
    return span;
  }
  eq(other: DictationGhostWidget) { return this.text === other.text; }
}

const dictationGhostField = StateField.define<DecorationSet>({
  create() { return Decoration.none; },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setDictationGhostEffect)) {
        if (effect.value === null) return Decoration.none;
        const { pos, text } = effect.value;
        if (!text) return Decoration.none;
        return Decoration.set([
          Decoration.widget({ widget: new DictationGhostWidget(text), side: 1 }).range(pos),
        ]);
      }
    }
    return decorations.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

interface EditorProps {
  editorViewRef: RefObject<EditorView | null>;
  onReadFrom: (target: { fromLine: number; fromChar: number }) => void;
}

export function Editor({ editorViewRef, onReadFrom }: EditorProps) {
  const { path: openFilePath, content: fileContent } = useOpenFile();
  const dispatch = useStoreDispatch();
  const loadFileContent = useLoadFileContent();
  const highlight = useHighlight();
  const { preferences } = useAuth();
  const simpleMode = Boolean(preferences.tts?.simpleMode);
  const ttsPlayback = useTtsPlayback();
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const playbackRef = useRef(ttsPlayback);
  playbackRef.current = ttsPlayback;
  const wrapCompartment = useRef(new Compartment());
  const [wordWrap, setWordWrap] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; line: number; fromChar: number } | null>(null);
  const lastWrittenContent = useRef<string>("");
  const lastBroadcastContent = useRef<string>("");
  const diskWriteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveBroadcastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLocalEdit = useRef(false);
  const lastManualViewportIntentAtRef = useRef(0);
  const lastAutoScrollAtRef = useRef(0);

  const runContextMenuAction = (action: () => void) => (
    event: ReactMouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  // Create/destroy editor
  useEffect(() => {
    if (!containerRef.current || fileContent === null) return;

    lastWrittenContent.current = fileContent;
    lastBroadcastContent.current = fileContent;

    const startState = EditorState.create({
      doc: fileContent,
      extensions: [
        lineNumbers(),
        highlightActiveLine(),
        highlightActiveLineGutter(),
        history(),
        bracketMatching(),
        highlightSelectionMatches(),
        autocompletion(),
        syntaxHighlighting(defaultHighlightStyle),
        markdown({ codeLanguages: languages }),
        highlightField,
        dictationGhostField,
        wrapCompartment.current.of(wordWrap ? EditorView.lineWrapping : []),
        EditorView.mouseSelectionStyle.of(safeMouseSelectionStyle),
        keymap.of([
          ...defaultKeymap,
          ...historyKeymap,
          ...searchKeymap,
          ...completionKeymap,
        ]),
        EditorView.updateListener.of((update) => {
          if (update.selectionSet || update.docChanged) {
            const pos = update.state.selection.main.head;
            const line = update.state.doc.lineAt(pos);
            dispatch({ type: "file:cursor", line: line.number, col: pos - line.from + 1 });
          }
          if (update.docChanged) {
            const content = update.state.doc.toString();
            dispatch({ type: "file:content", content });
            isLocalEdit.current = true;

            // Live broadcast to other clients (very fast)
            if (liveBroadcastTimer.current) clearTimeout(liveBroadcastTimer.current);
            liveBroadcastTimer.current = setTimeout(() => {
              if (!openFilePath) return;
              const latest = editorViewRef.current?.state.doc.toString();
              if (latest !== undefined && latest !== lastBroadcastContent.current) {
                lastBroadcastContent.current = latest;
                dispatch({ type: "file:live", path: openFilePath, content: latest });
              }
            }, LIVE_BROADCAST_DELAY);

            // Debounced disk write (less frequent)
            if (diskWriteTimer.current) clearTimeout(diskWriteTimer.current);
            diskWriteTimer.current = setTimeout(() => {
              if (!openFilePath) return;
              const latest = editorViewRef.current?.state.doc.toString();
              if (latest !== undefined && latest !== lastWrittenContent.current) {
                lastWrittenContent.current = latest;
                dispatch({ type: "file:write", path: openFilePath, content: latest });
              }
            }, DISK_WRITE_DELAY);
          }
        }),
        EditorView.domEventHandlers({
          pointerdown() {
            lastManualViewportIntentAtRef.current = Date.now();
            return false;
          },
          wheel() {
            lastManualViewportIntentAtRef.current = Date.now();
            return false;
          },
          touchstart() {
            lastManualViewportIntentAtRef.current = Date.now();
            return false;
          },
          contextmenu(event, view) {
            lastManualViewportIntentAtRef.current = Date.now();
            const pos = resolveContextMenuPos(view, event);
            if (pos === null) return false;
            const line = view.state.doc.lineAt(pos);
            const relativePos = Math.max(0, Math.min(line.length, pos - line.from));
            const wordStart = getWordStartOffset(line.text, relativePos);
            const fromChar = line.from + wordStart;
            event.preventDefault();
            setCtxMenu({
              x: event.clientX,
              y: event.clientY,
              line: line.number,
              fromChar,
            });
            return true;
          },
        }),
        EditorView.theme({
          "&": {
            backgroundColor: "#ffffff",
          },
          ".cm-content": {
            caretColor: "#c4956a",
            maxWidth: "760px",
            margin: "0 auto",
            padding: "2rem 1.5rem",
            fontFamily: "'Georgia', 'Times New Roman', serif",
            fontSize: "16px",
            lineHeight: "1.75",
          },
          ".cm-line": {
            padding: "0",
          },
          ".cm-ai-highlight": {
            backgroundColor: "rgba(196, 149, 106, 0.1)",
            borderLeft: "3px solid #c4956a",
          },
        }),
      ],
    });

    const view = new EditorView({
      state: startState,
      parent: containerRef.current,
    });

    editorViewRef.current = view;

    const onScroll = () => {
      if (Date.now() - lastAutoScrollAtRef.current <= AUTO_SCROLL_GRACE_MS) return;
      lastManualViewportIntentAtRef.current = Date.now();
    };
    view.scrollDOM.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      // Flush pending timers
      if (liveBroadcastTimer.current) clearTimeout(liveBroadcastTimer.current);
      if (diskWriteTimer.current) clearTimeout(diskWriteTimer.current);
      liveBroadcastTimer.current = null;
      diskWriteTimer.current = null;
      // Final disk write if needed
      if (editorViewRef.current && openFilePath) {
        const content = editorViewRef.current.state.doc.toString();
        if (content !== lastWrittenContent.current) {
          dispatch({ type: "file:write", path: openFilePath, content });
        }
      }
      view.scrollDOM.removeEventListener("scroll", onScroll);
      view.destroy();
      editorViewRef.current = null;
    };
  }, [openFilePath, fileContent === null]); // Only recreate on file change

  // Toggle word wrap
  useEffect(() => {
    if (!editorViewRef.current) return;
    editorViewRef.current.dispatch({
      effects: wrapCompartment.current.reconfigure(
        wordWrap ? EditorView.lineWrapping : []
      ),
    });
  }, [wordWrap]);

  // Handle external content updates (file:live / file:updated from other clients)
  useEffect(() => {
    if (!editorViewRef.current || fileContent === null || !openFilePath) return;

    // Skip if this was triggered by our own local edit
    if (isLocalEdit.current) {
      isLocalEdit.current = false;
      return;
    }

    const currentContent = editorViewRef.current.state.doc.toString();
    if (fileContent === currentContent) return;

    // Apply remote changes into the editor
    const view = editorViewRef.current;
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.length,
        insert: fileContent,
      },
    });
    lastWrittenContent.current = fileContent;
    lastBroadcastContent.current = fileContent;
  }, [fileContent]);

  // Close context menu on click outside / escape
  useEffect(() => {
    if (!ctxMenu) return;
    const onClick = () => setCtxMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCtxMenu(null);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [ctxMenu]);

  // Handle AI / TTS paragraph highlight
  const lastScrolledLine = useRef<number | null>(null);
  useEffect(() => {
    const view = editorViewRef.current;
    if (!view) return;

    if (highlight) {
      const doc = view.state.doc;
      if (!doc.lines) return;
      const fromLine = Math.max(1, Math.min(highlight.fromLine, doc.lines));
      const toLine = Math.max(fromLine, Math.min(highlight.toLine, doc.lines));
      const from = doc.line(fromLine).from;
      const to = doc.line(toLine).to;

      const effects: any[] = [setHighlightEffect.of({ from, to })];
      if (lastScrolledLine.current !== fromLine) {
        lastScrolledLine.current = fromLine;
        const recentlyInteracted =
          Date.now() - lastManualViewportIntentAtRef.current <
          VIEWPORT_INTERACTION_COOLDOWN_MS;
        const shouldAutoScroll =
          !recentlyInteracted &&
          !isPosComfortablyVisible(view, from, wrapperRef.current);

        if (shouldAutoScroll) {
          lastAutoScrollAtRef.current = Date.now();
          effects.push(
            EditorView.scrollIntoView(from, {
              y:
                highlight.fromChar !== undefined || highlight.toChar !== undefined
                  ? "nearest"
                  : "center",
            })
          );
        }
      }
      view.dispatch({ effects });
    } else {
      lastScrolledLine.current = null;
      view.dispatch({ effects: setHighlightEffect.of(null) });
    }
  }, [highlight]);

  // Animated TTS cursor
  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    if (simpleMode || !ttsPlayback) {
      cursor.style.opacity = "0";
      return;
    }

    let animId: number;
    const animate = () => {
      const pb = playbackRef.current;
      const view = editorViewRef.current;
      const wrapper = wrapperRef.current;
      if (!pb || !view || !cursor || !wrapper) return;

      const now = Date.now();
      const elapsed = pb.playing
        ? pb.elapsed + (now - pb.dispatchedAt) / 1000
        : pb.elapsed;
      const progress = Math.min(1, Math.max(0, elapsed / pb.duration));
      const charPos = Math.floor(pb.fromChar + progress * (pb.toChar - pb.fromChar));
      const clampedPos = Math.min(charPos, view.state.doc.length);

      if (clampedPos < view.viewport.from || clampedPos > view.viewport.to) {
        cursor.style.opacity = "0";
      } else {
        const coords = view.coordsAtPos(clampedPos);
        if (coords) {
          const rect = wrapper.getBoundingClientRect();
          cursor.style.opacity = "1";
          cursor.style.transform = `translate(${coords.left - rect.left}px, ${coords.top - rect.top}px)`;
          cursor.style.height = `${coords.bottom - coords.top}px`;
        } else {
          cursor.style.opacity = "0";
        }
      }

      if (progress < 1 && pb.playing) {
        animId = requestAnimationFrame(animate);
      }
    };

    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [simpleMode, ttsPlayback]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-surface border-b border-border text-xs">
        <span className="text-text-muted">{openFilePath}</span>
        <button
          onClick={() => setWordWrap((w) => !w)}
          className={`px-2 py-0.5 rounded transition-colors ${
            wordWrap ? "text-accent" : "text-text-muted hover:text-text"
          }`}
          title={wordWrap ? "Disable word wrap" : "Enable word wrap"}
        >
          Wrap
        </button>
      </div>

      {/* Editor container */}
      <div ref={wrapperRef} className="flex-1 overflow-hidden relative">
        <div ref={containerRef} className="h-full" />
        <div ref={cursorRef} className="tts-cursor" style={{ opacity: 0 }} />
      </div>

      {/* Toast */}
      {toast && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-text text-surface px-4 py-2 rounded-xl text-sm shadow-lg toast-enter flex items-center gap-3">
          <span>{toast}</span>
          {toast.includes("reload") && (
            <button
              onClick={() => {
                if (openFilePath) loadFileContent(openFilePath);
                setToast(null);
              }}
              className="text-accent font-medium hover:underline"
            >
              Reload
            </button>
          )}
          <button
            onClick={() => setToast(null)}
            className="text-text-muted hover:text-surface ml-1"
          >
            ×
          </button>
        </div>
      )}

      {/* Context menu */}
      {ctxMenu && (
        <div
          className="fixed z-50 bg-surface border border-border rounded-lg shadow-lg py-1 min-w-[180px]"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-text hover:bg-surface-alt transition-colors flex items-center gap-2"
            onMouseDown={runContextMenuAction(() => {
              onReadFrom({
                fromLine: ctxMenu.line,
                fromChar: ctxMenu.fromChar,
              });
              setCtxMenu(null);
            })}
          >
            <Play size={14} weight="fill" className="shrink-0" />
            Read from here
          </button>
          <div className="my-1 border-t border-border" />
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-text hover:bg-surface-alt transition-colors flex items-center gap-2"
            onMouseDown={runContextMenuAction(() => {
              const lineText = editorViewRef.current?.state.doc.line(ctxMenu.line).text.trim();
              const snippet = lineText && lineText.length > 60 ? lineText.slice(0, 60) + "..." : lineText;
              dispatch({ type: "chat:prompt", message: `Fact-check this: "${snippet || `line ${ctxMenu.line}`}"` });
              setCtxMenu(null);
            })}
          >
            <MagnifyingGlass size={14} className="shrink-0" />
            Fact check this
          </button>
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-text hover:bg-surface-alt transition-colors flex items-center gap-2"
            onMouseDown={runContextMenuAction(() => {
              const fileName = openFilePath?.split("/").pop() ?? "this file";
              dispatch({ type: "chat:prompt", message: `Review the writing in ${fileName} around line ${ctxMenu.line}. Suggest improvements for clarity and style.` });
              setCtxMenu(null);
            })}
          >
            <PencilLine size={14} className="shrink-0" />
            Review the writing
          </button>
        </div>
      )}
    </div>
  );
}
